import { DocumentTextIcon } from "@heroicons/react/outline";
import { DataTable, Column } from "components/Table";
import StatusBadge from "pages/crawler/components/jobs/StatusBadge";
import JobActionsDropdown from "pages/crawler/components/jobs/JobActionsDropdown";
import { CrawlerJob } from "pages/crawler/components/jobs/types";
import TruncatedText from "components/TruncatedText";
import { formatLastRun } from "pages/crawler/utils/crawlerDisplayUtils";

const EmptyJobs = () => (
  <div className="flex flex-col items-center justify-center">
    <DocumentTextIcon className="w-16 h-16 text-gray-300 mb-4" />
    <h3 className="text-lg font-medium text-gray-900 mb-1">No jobs yet</h3>
    <p className="text-sm text-gray-500 max-w-md">
      Jobs appear here once the crawler runs. Older jobs are removed after 7 days.
    </p>
  </div>
);

export const CrawlerJobsTable = ({
  jobs,
  outputFormat,
  onRowClick,
  onJobAction,
}: {
  jobs: CrawlerJob[];
  outputFormat?: string;
  onRowClick: (job: CrawlerJob) => void;
  onJobAction: (action: string, jobId: string) => void;
}) => {
  const columns: Column<CrawlerJob>[] = [
    {
      key: "id",
      header: "ID",
      tooltip: "Job identifier",
      cell: (job) => job.id,
      cellClassName: "whitespace-nowrap font-medium text-gray-900",
    },
    {
      key: "lastRun",
      header: "Last run",
      tooltip: "When the job was last executed",
      cell: (job) => formatLastRun(job.finishedAt, { fallback: "-", includeSeconds: false }),
      cellClassName: "whitespace-nowrap text-gray-500",
    },
    {
      key: "status",
      header: "Status",
      tooltip: "Current job status",
      cell: (job) => <StatusBadge status={job.status} />,
      cellClassName: "whitespace-nowrap",
    },
    {
      key: "domain",
      header: "Domain",
      tooltip: "Target domain for the job",
      cell: (job) => <TruncatedText text={job.startUrl || ""} maxWidth="360px" />,
      cellClassName: "text-gray-900",
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (job) => (
        <JobActionsDropdown jobId={job.id} jobStatus={job.status} outputFormat={outputFormat} onAction={onJobAction} />
      ),
      cellClassName: "whitespace-nowrap font-medium",
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={jobs}
      getRowKey={(job, idx) => `${job.id}-${idx}`}
      onRowClick={onRowClick}
      empty={<EmptyJobs />}
    />
  );
};

export default CrawlerJobsTable;
